import React, { useMemo } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useCfoProgramProgress } from '../../../hooks/analytics/useCfoReports.ts';
import { ProgressBar, WidgetCard, formatNumber } from '../controller/shared.tsx';
import { CHART_TOOLTIP_STYLE, chartColorFor } from '../theme.ts';

const COMPLETE_RESULTS = new Set(['effective', 'passed', 'ineffective', 'failed']);

/**
 * Report #4 — Program Progress Summary (proxy — workflow/conclusion
 * distribution; full assignee-level progress needs the tester-persona view
 * on `test_runs.assignee_user_id`, per the backend's own doc comment).
 */
export const ProgramProgressPanel: React.FC = () => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useCfoProgramProgress();
  const rows = data ?? [];

  const total = rows.reduce((sum, r) => sum + r.controls, 0);
  const complete = rows
    .filter((r) => COMPLETE_RESULTS.has((r.result ?? '').toLowerCase()))
    .reduce((sum, r) => sum + r.controls, 0);
  const pct = total > 0 ? (100 * complete) / total : 0;

  const donutData = useMemo(
    () =>
      rows.map((r) => ({
        name: (r.result ?? 'unknown').replace(/_/g, ' '),
        key: (r.result ?? 'unknown').toLowerCase(),
        value: r.controls,
      })),
    [rows]
  );

  return (
    <WidgetCard
      title="Program Progress Summary"
      description="Control testing progress across the current cycle (proxy view)"
      loading={isLoading}
      error={isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load program progress.'}
      isEmpty={!isLoading && !isError && rows.length === 0}
      emptyMessage="No controls found."
      updatedAt={dataUpdatedAt}
      onRefresh={refetch}
      isFetching={isFetching}
    >
      <div className="mb-4">
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-slate-500">Overall completion</span>
          <span className="tabular-nums font-semibold text-slate-900">{pct.toFixed(0)}%</span>
        </div>
        <ProgressBar pct={pct} colorClass="bg-emerald-500" />
        <p className="text-xs text-slate-400 mt-1">
          {formatNumber(complete)} of {formatNumber(total)} controls have a concluded test
        </p>
      </div>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={donutData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={3}>
              {donutData.map((entry) => (
                <Cell key={entry.key} fill={chartColorFor(entry.key)} stroke="#fff" strokeWidth={2} />
              ))}
            </Pie>
            <Tooltip formatter={(value: number, name: string) => [`${value} controls`, name]} contentStyle={CHART_TOOLTIP_STYLE} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </WidgetCard>
  );
};

export default ProgramProgressPanel;
